'use client'

import { useMemo } from 'react'

import { Icon, IconComponent, Tooltip } from '@graftcode/design-system'

import { GRAFTABILITY_WARNING_ICON_CLASS } from '@/core/components/GraftabilityRow'
import { useLibraries } from '@/core/hooks'
import { useCodeStore } from '@/core/stores/codeStore'
import { useNavigationPath } from '@/core/stores/navigationStore'
import type { TypeUsage } from '@/core/types/library.type'
import {
  analyzeTypeUsageGraftability,
  buildHostedTypeFqns,
  buildHostingLibraryContext,
} from '@/core/utils/graftability'
import { getGraftabilityIssueSummary } from '@/core/utils/graftability/formatGraftabilityIssue'

import styles from './TypeGraftabilityHint.module.scss'

interface TypeGraftabilityHintProps {
  typeUsage?: TypeUsage
}

export function TypeGraftabilityHint({ typeUsage }: TypeGraftabilityHintProps) {
  const selectedTechnology = useCodeStore((state) => state.selectedTechnology)
  const navigationPath = useNavigationPath()
  const { libraries } = useLibraries()

  const analysisOptions = useMemo(() => {
    const libraryName = navigationPath[0]
    const libraryData = libraryName && libraries ? libraries[libraryName] : undefined
    if (!libraryData) return undefined
    return {
      hostedTypeFqns: buildHostedTypeFqns(libraryData),
      hostingLibraryContext: buildHostingLibraryContext(libraryData),
    }
  }, [navigationPath, libraries])

  const issues = useMemo(() => {
    if (!typeUsage) return []
    return analyzeTypeUsageGraftability(typeUsage, {
      technology: selectedTechnology,
      ...analysisOptions,
    }).issues
  }, [typeUsage, selectedTechnology, analysisOptions])

  if (issues.length === 0) return null

  const tooltip = issues.map((issue) => getGraftabilityIssueSummary(issue)).join(' ')

  return (
    <Tooltip content={tooltip}>
      <span className={styles.hint} aria-label="Type cannot be grafted">
        <IconComponent icon={Icon.WarningFilled} size={14} className={GRAFTABILITY_WARNING_ICON_CLASS} />
      </span>
    </Tooltip>
  )
}
